import { format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

export const TRIAL_TIME_DISPLAY_PATTERN = 'h:mm a';
export const TRIAL_DATE_DISPLAY_PATTERN = 'MMM dd, yyyy';
export const TRIAL_CARD_DATE_PATTERN = 'MMMM d, yyyy';
export const TRIAL_DAY_OF_WEEK_PATTERN = 'EEE';

const LEGACY_LOCAL_ISO_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

function hasTimezoneOffset(value: string): boolean {
  return value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value);
}

/**
 * Parses a trial appointment instant from API data.
 * Handles UTC ISO (preferred) and legacy timezone-naive studio-local strings (ENR-777).
 */
export function parseTrialInstant(
  value: string | null | undefined,
  studioTimezone?: string,
): Date | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const isLegacyLocalIso =
    LEGACY_LOCAL_ISO_PATTERN.test(trimmed) && !hasTimezoneOffset(trimmed);

  if (isLegacyLocalIso && studioTimezone) {
    try {
      const localDateTime = trimmed.replace(/\.\d+$/, '');
      const zoned = fromZonedTime(localDateTime, studioTimezone);
      if (isValid(zoned)) {
        return zoned;
      }
    } catch {
      /* fall through to parseISO fallback */
    }
  }

  const parsed = parseISO(trimmed);
  return isValid(parsed) ? parsed : null;
}

export function formatTrialDateTime(
  value: string | null | undefined,
  studioTimezone: string | null | undefined,
  pattern: string,
): string | null {
  const instant = parseTrialInstant(value, studioTimezone ?? undefined);
  if (!instant) return null;

  if (studioTimezone) {
    try {
      return formatInTimeZone(instant, studioTimezone, pattern);
    } catch {
      /* fall through */
    }
  }

  try {
    return format(instant, pattern);
  } catch {
    return null;
  }
}

export function formatTrialTime(
  value: string | null | undefined,
  studioTimezone?: string | null,
  pattern = TRIAL_TIME_DISPLAY_PATTERN,
): string | null {
  return formatTrialDateTime(value, studioTimezone ?? undefined, pattern);
}

export function formatTrialDate(
  value: string | null | undefined,
  studioTimezone?: string | null,
  pattern = TRIAL_DATE_DISPLAY_PATTERN,
): string | null {
  return formatTrialDateTime(value, studioTimezone ?? undefined, pattern);
}

export function formatTrialTimeDisplay(
  value: string | null | undefined,
  studioTimezone?: string | null,
): string {
  return formatTrialTime(value, studioTimezone) ?? '-';
}

export function formatTrialDateDisplay(
  value: string | null | undefined,
  studioTimezone?: string | null,
): string {
  return formatTrialDate(value, studioTimezone) ?? '-';
}

export function formatTrialDayOfWeek(
  value: string | null | undefined,
  studioTimezone?: string | null,
): string | null {
  return formatTrialDateTime(
    value,
    studioTimezone ?? undefined,
    TRIAL_DAY_OF_WEEK_PATTERN,
  );
}

export function formatTrialTimeRange(
  start: string | null | undefined,
  end: string | null | undefined,
  studioTimezone?: string | null,
): string | null {
  const formattedStart = formatTrialTime(start, studioTimezone);
  const formattedEnd = formatTrialTime(end, studioTimezone);
  if (!formattedStart || !formattedEnd) return null;
  return `${formattedStart} - ${formattedEnd}`;
}
